import DynamicForm from "@/common/DynamicForm";
import { IntakeSectionHeader, StyledGridRow } from "@/common/styles";
import { Form } from "antd";
import { useEffect, useRef, useState } from "react";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import { useDispatch, useSelector } from "react-redux";
import { setBookings } from "@/slices/bookingsSlice";
import {
  convertApiFields,
  isNonEmptyVariable,
  hasCheckSecurity,
  getSQLTypeToHTMLType,
  debounce,
} from "@/util/common_function";
import dayjs from "dayjs";

// BioExtra's own PK/FK columns, lower-first — mirrors how the Warrants
// module addresses WarrantsExtra's warrantExtraID/warrantID (see
// AdditionalWarrantInformation.jsx).
const BIO_EXTRA_ID_KEY = "bioExtraID";
const BIO_ID_COLUMN = "bioID";

const AdditionalPersonalInformation = ({ header, onFieldsLoaded }) => {
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings,
  );
  const [form] = Form.useForm();
  const [apiFields, setApiFields] = useState([]);
  const [additionalPersonalInfo, setAdditionalPersonalInfo] = useState(null);
  // True once fieldHtmlTypeMapRef is populated — gates the effect below so
  // a date/datetime column's value is never handed to the form as a plain
  // string. Without this, a bookings update landing before the field-defs
  // GET resolves would set e.g. DNA_Test_Date_Completed_U as a raw string;
  // antd's DatePicker calls value.isValid() once its Form.Item mounts with
  // that stale value and crashes ("date.isValid is not a function") since
  // a string has no such method.
  const [fieldsReady, setFieldsReady] = useState(false);
  const fullSearch = useSelector((state) => state.fullSearch);

  const bookingsRef = useRef(bookings);
  useEffect(() => {
    bookingsRef.current = bookings;
  }, [bookings]);

  // columnName -> htmlType ("date" / "datetime" / ...) for every BioExtra
  // field — same typeMap-driven approach ArrestBook.jsx uses for
  // ArrestReportsExtra's dynamic fields, so date/datetime columns get
  // dayjs<->string converted consistently on both sides of the form.
  const fieldHtmlTypeMapRef = useRef({});

  // --- Fetch API Field Definitions ---
  useEffect(() => {
    getFieldsFromExtraAPI();
  }, []);

  // The saved BioExtra row itself (with its real PK) is fetched once,
  // centrally, by useBioExtraSync in BookingsDetails/index.jsx — not here.
  // A second copy of that fetch used to live in this component too; since
  // each had its own private "in flight" guard, unaware of the other, both
  // would race a GET before either's response landed in redux, so the two
  // never actually deduplicated against each other.

  useEffect(() => {
    // Wait for fieldHtmlTypeMapRef to be populated before touching the form
    // — see the fieldsReady comment above for why.
    if (!fieldsReady) return;

    if (isNonEmptyVariable(bookings)) {
      const apin = bookings?.bio?.[0]?.bioExtra?.[0];
      // The DatePicker needs an actual dayjs instance to render a
      // date/datetime field's value — redux holds it as a plain string
      // (from the GET fetch, or already-formatted from a prior change), so
      // convert just the known date/datetime columns here rather than
      // guessing at any string that happens to look like a date.
      const withDates = apin
        ? Object.keys(apin).reduce((acc, key) => {
            const val = apin[key];
            const htmlType = fieldHtmlTypeMapRef.current[key];
            const isDateField = htmlType === "date" || htmlType === "datetime";
            acc[key] =
              isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
            return acc;
          }, {})
        : apin;
      setAdditionalPersonalInfo(withDates);
    } else {
      setAdditionalPersonalInfo(null);
    }
  }, [bookings, fieldsReady]);

  // Set the form’s initial values from formData (after transforming for display).
  useEffect(() => {
    if (isNonEmptyVariable(additionalPersonalInfo)) {
      form.setFieldsValue(additionalPersonalInfo);
    } else {
      form.resetFields();
    }
  }, [additionalPersonalInfo]);

  const getFieldsFromExtraAPI = () => {
    api
      .get(API_ENDPOINTS.getBioExtra)
      .then((response) => {
        const tableData = response?.data?.filter((d) =>
          d?.columnName?.endsWith("_U"),
        );
        let converted = convertApiFields(tableData, "BioExtra");
        console.log(converted);

        const typeMap = {};
        converted.forEach((c) => {
          typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
        });
        fieldHtmlTypeMapRef.current = typeMap;

        setApiFields(converted);
        setFieldsReady(true);
        onFieldsLoaded?.(converted.length > 0);
      })
      .catch((error) => {
        console.error(
          "Request failed:",
          error.response ? error.response.data : error.message,
        );
        onFieldsLoaded?.(false);
      });
  };

  // Merge current visible form values with hidden fields from formData, then
  // transform keys before saving. Reads bookingsRef (not the `bookings`
  // component-scope variable) since this is called from a debounced
  // callback created once (see dataModifyRef below) — the ref always holds
  // the latest redux value, avoiding a stale closure.
  const updateForm = (currentValues) => {
    const bio = bookingsRef.current?.bio?.[0] || {};
    const existingExtra = bio?.bioExtra?.[0] || {};

    // A date/datetime field's value is a live dayjs instance here — redux
    // must never hold on to one of those (it doesn't round-trip through
    // Redux/Immer's state the way a plain string does, which is what was
    // making the picked date disappear again on the next render). Format it
    // to a plain string before it leaves the form, same as ArrestBook.jsx's
    // dataModify does for ArrestReportsExtra's dynamic date/datetime fields
    // (and clear it to null rather than leaving it out, so an emptied
    // field actually clears in redux instead of sticking around).
    const formattedValues = Object.keys(currentValues).reduce((acc, key) => {
      const val = currentValues[key];
      const htmlType = fieldHtmlTypeMapRef.current[key];

      if (val) {
        if (htmlType === "date") {
          acc[key] = dayjs(val).format("MM/DD/YYYY");
        } else if (htmlType === "datetime") {
          acc[key] = dayjs(val).format("MM/DD/YYYY HH:mm");
        } else {
          acc[key] = val;
        }
      } else {
        acc[key] = null;
      }
      return acc;
    }, {});

    dispatch(
      setBookings({
        bio: [
          {
            ...bio,
            bioExtra: [
              {
                ...existingExtra,
                ...formattedValues,
                [BIO_EXTRA_ID_KEY]: existingExtra?.[BIO_EXTRA_ID_KEY] ?? 0,
                [BIO_ID_COLUMN]:
                  existingExtra?.[BIO_ID_COLUMN] ?? bio?.bioId ?? 0,
              },
            ],
          },
        ],
      }),
    );
  };

  // Debounced so rapid keystrokes/onFieldsChange events don't dispatch on
  // every single one — same pattern as ArrestBook.jsx's dataModify. Built
  // once via useRef rather than redeclared each render.
  const dataModifyRef = useRef(null);
  if (!dataModifyRef.current) {
    dataModifyRef.current = debounce(() => {
      updateForm(form.getFieldsValue());
    }, 800);
  }

  return (
    <>
      {header && (
        <IntakeSectionHeader>
          <span>{header}</span>
        </IntakeSectionHeader>
      )}
      <Form
        form={form}
        onFieldsChange={() => {
          dataModifyRef.current();
        }}
        disabled={!bookingsFieldsEnabled}
      >
        <StyledGridRow gutter={[16]}>
          {apiFields.map((sf, index) => {
            const view = hasCheckSecurity(
              "Functionality",
              "field",
              "VIEW",
              "",
              "",
              sf.tableName,
              sf.columnName,
              "Bookings",
            );

            const edit = hasCheckSecurity(
              "Functionality",
              "field",
              "MODIFY",
              "",
              "",
              sf.tableName,
              sf.columnName,
              "Bookings",
            );

            return (
              <DynamicForm
                key={index}
                formAttributes={sf}
                form={form}
                bookingsFieldsEnabled={bookingsFieldsEnabled}
                fullSearch={fullSearch}
                view={view}
                edit={edit}
              />
            );
          })}
        </StyledGridRow>
      </Form>
    </>
  );
};

export default AdditionalPersonalInformation;
